import React, { useState, useEffect, useRef } from 'react';
import { Upload, Search, Save, FileText, X, RefreshCw, Database } from 'lucide-react';
import ExcelImageExtractor from './ExcelImageExtractor';

const API_BASE = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? `http://${window.location.hostname}:3002`
        : 'http://localhost:3002'
);

// ⭐ 문서 종류 상수 (레거시 시스템과 동일 - 텍스트 이름 직접 저장)
const DOCUMENT_TYPES = [
    '사업자등록증',
    '계약서',
    '신분증',
    '등록증',
    '말소등록증',
    '면장',
    '송금증',
    '인보이스',
    '통관서류',
    '폐차증',
    '필증',
    '환급관련서류',
    '정정관련서류',
    '취하관련서류',
    '경락확인서',
    '계산서',
    '등록원부',
    '기타'
];

const LegacyCustomerRegistration = ({ API_BASE_URL }) => {
    const [formData, setFormData] = useState({
        businessNumber: '',
        companyType: '법인',
        companyName: '',
        companyNameEng: '',
        ceoName: '',
        ceoNameEng: '',
        address: '',
        addressEng: '',
        tel: '',
        fax: '',
        gwan: '선택',
        refundRate: '30',
        saleRate: '50',
        contractSeal: null,
        invoiceSeal: null
    });

    const [attachments, setAttachments] = useState([]);
    const [selectedCompanyNo, setSelectedCompanyNo] = useState(null);

    // Grid State
    const [companyList, setCompanyList] = useState([]);
    const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
    const gridRef = useRef(null);
    const gridId = 'legacy-company-grid';

    // 홈택스 비교 모달 AUIGrid
    const homeTaxLeftGridRef = useRef(null);
    const homeTaxRightGridRef = useRef(null);
    const homeTaxLeftGridId = 'hometax-left-grid';
    const homeTaxRightGridId = 'hometax-right-grid';

    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // 홈택스 비교 모달 상태
    const [isHomeTaxModalOpen, setIsHomeTaxModalOpen] = useState(false);
    const [homeTaxCompareData, setHomeTaxCompareData] = useState(null); // { current: {...}, fetched: {...}, companyNo: ... }
    const [selectedHomeTaxIndex, setSelectedHomeTaxIndex] = useState(0);
    const [checkedItems, setCheckedItems] = useState([]); // 체크된 항목 인덱스 배열
    const [reflectedItems, setReflectedItems] = useState([]); // 반영 완료된 항목 인덱스 배열
    const [translationResults, setTranslationResults] = useState({}); // 인덱스별 번역 결과

    // 영문 변환 상태
    const [translationData, setTranslationData] = useState({
        companyNameEng: '',
        ceoNameEng: '',
        addressEng: ''
    });
    const [isTranslating, setIsTranslating] = useState(false);

    // 진행 상태
    const [progress, setProgress] = useState({ current: 0, total: 0, action: '' });

    // 일괄변경 모달 상태
    const [isBatchUpdateModalOpen, setIsBatchUpdateModalOpen] = useState(false);
    const [batchUpdateData, setBatchUpdateData] = useState({
        gwan: '',
        rate: '',
        saleRate: ''
    });

    // 홈택스 확인 모달 상태
    const [homeTaxConfirmItems, setHomeTaxConfirmItems] = useState([]); // 확인용 선택 아이템


    // 1. Fetch Company List
    const fetchCompanyList = async () => {
        try {
            console.log('Fetching company list...');
            const groupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode');
            if (!groupCode) {
                console.error('❌ groupCode가 없습니다. 다시 로그인하세요.');
                alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
                return;
            }
            console.log('📋 Using groupCode:', groupCode);
            const res = await fetch(`${API_BASE}/api/legacy/companies?groupCode=${groupCode}`);
            const json = await res.json();
            console.log('Company list response:', json);

            if (json.success) {
                const list = json.list || [];
                setCompanyList(list);
                if (window.AUIGrid && window.AUIGrid.isCreated(`#${gridId}`)) {
                    window.AUIGrid.setGridData(`#${gridId}`, list);
                }
            } else {
                console.error('API reported failure:', json.error);
            }
        } catch (err) {
            console.error('Failed to fetch company list:', err);
        }
    };

    // 첨부파일 목록 조회
    const fetchAttachments = async (companyNo) => {
        if (!companyNo) {
            setAttachments([]);
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/legacy/company/${companyNo}/attachments`);
            const json = await res.json();
            if (json.success) {
                setAttachments(json.attachments || []);
            } else {
                setAttachments([]);
            }
        } catch (err) {
            console.error('Failed to fetch attachments:', err);
            setAttachments([]);
        }
    };

    // 첨부파일 다운로드
    const handleDownload = async (attachment) => {
        if (!attachment.filePath) {
            alert('파일 경로가 없습니다.');
            return;
        }
        const url = `${API_BASE}/api/legacy/attachment/download?filePath=${encodeURIComponent(attachment.filePath)}&fileName=${encodeURIComponent(attachment.originalName || 'download.png')}`;
        window.open(url, '_blank');
    };

    // 첨부파일 업로드 (type은 텍스트 이름: "사업자등록증", "기타" 등)
    const handleAttachmentUpload = async (e, typeName = '기타') => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!selectedCompanyNo) {
            alert('먼저 거래처를 선택해주세요.');
            return;
        }

        const formDataObj = new FormData();
        formDataObj.append('file', file);
        formDataObj.append('type', typeName);  // 텍스트 이름 전송

        try {
            const res = await fetch(`${API_BASE}/api/legacy/company/${selectedCompanyNo}/attachments`, {
                method: 'POST',
                body: formDataObj
            });
            const json = await res.json();
            if (json.success) {
                alert('파일 업로드 완료');
                fetchAttachments(selectedCompanyNo); // 목록 새로고침
            } else {
                alert(`업로드 실패: ${json.error}`);
            }
        } catch (err) {
            console.error('Upload error:', err);
            alert('업로드 중 오류가 발생했습니다.');
        }

        // input 초기화
        e.target.value = '';
    };

    // 첨부파일 타입 변경 (type은 텍스트 이름)
    const handleAttachmentTypeChange = async (attachmentId, newTypeName) => {
        try {
            const res = await fetch(`${API_BASE}/api/legacy/attachment/${attachmentId}/type`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: newTypeName })
            });
            const json = await res.json();
            if (json.success) {
                fetchAttachments(selectedCompanyNo); // 목록 새로고침
            } else {
                alert(`타입 변경 실패: ${json.error}`);
            }
        } catch (err) {
            console.error('Type change error:', err);
        }
    };

    // 첨부파일 삭제
    const handleAttachmentDelete = async (attachmentId) => {
        if (!attachmentId) {
            alert('삭제할 파일을 선택해주세요.');
            return;
        }

        if (!window.confirm('정말 삭제하시겠습니까?')) return;

        try {
            const res = await fetch(`${API_BASE}/api/legacy/attachment/${attachmentId}`, {
                method: 'DELETE'
            });
            const json = await res.json();
            if (json.success) {
                alert('삭제 완료');
                fetchAttachments(selectedCompanyNo); // 목록 새로고침
            } else {
                alert(`삭제 실패: ${json.error}`);
            }
        } catch (err) {
            console.error('Delete error:', err);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    // 홈택스 자료 가져오기 - 1단계: 확인 모달 열기
    const handleFetchHomeTax = () => {
        if (!window.AUIGrid) return;

        const checkedItems = window.AUIGrid.getCheckedRowItems(`#${gridId}`);
        if (!checkedItems || checkedItems.length === 0) {
            alert('홈택스 자료를 가져올 거래처를 체크해주세요.');
            return;
        }

        // 사업자번호 유효성 검사
        const validItems = checkedItems.filter(item => {
            const bno = item.item.BUSINESS_NUMBER?.replace(/[^0-9]/g, '');
            return bno && bno.length === 10;
        });

        if (validItems.length === 0) {
            alert('유효한 사업자번호(10자리)가 있는 거래처가 없습니다.');
            return;
        }

        // 확인 모달에 표시할 아이템 저장
        setHomeTaxConfirmItems(validItems.map(v => v.item));
    };

    // 홈택스 자료 가져오기 - 2단계: 실제 API 호출
    const handleExecuteHomeTaxFetch = async () => {
        if (homeTaxConfirmItems.length === 0) return;

        setLoading(true);

        // 모든 선택된 건에 대해 조회
        const compareList = [];

        for (const item of homeTaxConfirmItems) {
            const businessNumber = item.BUSINESS_NUMBER.replace(/[^0-9]/g, '');

            try {
                const res = await fetch(`${API_BASE}/api/legacy/hometax-lookup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ businessNumber })
                });
                const json = await res.json();

                compareList.push({
                    companyNo: item.COMPANY_NO,
                    businessNumber: item.BUSINESS_NUMBER,
                    current: {
                        법인개인: item.BUSINESS_TYPE || '',
                        상호: item.SANGHO || '',
                        대표자: item.CEO || '',
                        주소: item.ADDRESS || '',
                        전화번호: item.TEL || '',
                        팩스번호: item.FAX || ''
                    },
                    fetched: json.success && json.data ? {
                        법인개인: json.data.법인구분 || '',
                        상호: json.data.사업자명 || '',
                        대표자: json.data.대표자명 || '',
                        주소: json.data.도로명주소 || '',
                        전화번호: json.data.전화번호 || '',
                        팩스번호: ''
                    } : null,
                    error: json.success ? null : (json.error || '조회 실패'),
                    applied: false
                });
            } catch (err) {
                compareList.push({
                    companyNo: item.COMPANY_NO,
                    businessNumber: item.BUSINESS_NUMBER,
                    current: {
                        법인개인: item.BUSINESS_TYPE || '',
                        상호: item.SANGHO || '',
                        대표자: item.CEO || '',
                        주소: item.ADDRESS || '',
                        전화번호: item.TEL || '',
                        팩스번호: item.FAX || ''
                    },
                    fetched: null,
                    error: err.message,
                    applied: false
                });
            }
        }

        setLoading(false);
        setHomeTaxConfirmItems([]); // 확인 모달 닫기

        if (compareList.length > 0) {
            setHomeTaxCompareData(compareList);
            // 모든 항목을 기본 체크
            setCheckedItems(compareList.map((_, idx) => idx));
            setReflectedItems([]); // 반영된 항목 초기화
            setTranslationResults({});
            setIsHomeTaxModalOpen(true);
        } else {
            alert('조회된 데이터가 없습니다.');
        }
    };

    // 법인개인 유형 변환 함수 (handleApplyHomeTaxItem에서 사용)
    // DB 저장 시: 법인='A', 개인='B'
    const getCompanyTypeForSave = (type, companyName) => {
        if (!type && !companyName) return 'A'; // 기본값 법인
        // 홈택스 법인개인 값: "영리법인의본점", "개인" 등
        if (type?.includes('법인')) return 'A';
        // 상호명에 (주), (유), (합), (사) 포함시 법인
        if (companyName && /\(주\)|\(유\)|\(합\)|\(사\)|주식회사|유한회사/.test(companyName)) return 'A';
        return 'B'; // 개인
    };

    // 홈택스 데이터 적용 (선택 건) - DB에 저장
    const handleApplyHomeTaxItem = async (index) => {
        if (!homeTaxCompareData || !homeTaxCompareData[index]) return;

        const item = homeTaxCompareData[index];
        if (!item.fetched) {
            alert('조회된 데이터가 없습니다.');
            return;
        }

        setLoading(true);
        try {
            const companyType = getCompanyTypeForSave(item.fetched.법인개인, item.fetched.상호);
            // ⭐ 번역 결과 가져오기
            const trans = translationResults[index] || {};

            const res = await fetch(`${API_BASE}/api/legacy/company`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    businessNumber: item.businessNumber,
                    groupCode: localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode'), // ⚠️ 필수
                    companyType: companyType,
                    companyName: item.fetched.상호,
                    ceoName: item.fetched.대표자,
                    address: item.fetched.주소,
                    tel: item.fetched.전화번호 || '',
                    // ⭐ 영문 정보 추가
                    companyNameEng: trans.companyNameEng || '',
                    ceoNameEng: trans.ceoNameEng || '',
                    addressEng: trans.addressEng || ''
                })
            });

            if (res.ok) {
                // 상태 업데이트
                const updated = [...homeTaxCompareData];
                updated[index].applied = true;
                setHomeTaxCompareData(updated);
                alert('해당 자료가 저장되었습니다.');

                // 그리드 업데이트
                if (window.AUIGrid && window.AUIGrid.isCreated(`#${homeTaxRightGridId}`)) {
                    window.AUIGrid.updateRow(`#${homeTaxRightGridId}`, { STATUS: '적용' }, index);
                }
            } else {
                alert('저장에 실패했습니다.');
            }
        } catch (err) {
            console.error('Save error:', err);
            alert('저장 중 오류가 발생했습니다.');
        }
        setLoading(false);
    };

    // 전체 적용
    const handleApplyAllHomeTax = async () => {
        if (!homeTaxCompareData) return;

        const toApply = homeTaxCompareData.filter(item => item.fetched && !item.error);

        if (toApply.length === 0) {
            alert('적용할 데이터가 없습니다.');
            return;
        }

        setLoading(true);
        setProgress({ current: 0, total: toApply.length, action: '적용' });

        // DB에 직접 저장 (기존 saveCompany API 사용 - businessNumber 기준 UPSERT)
        let successCount = 0;
        let processed = 0;

        for (let i = 0; i < homeTaxCompareData.length; i++) {
            const item = homeTaxCompareData[i];
            if (!item.fetched || item.error) continue;

            try {
                const companyType = getCompanyTypeForSave(item.fetched.법인개인, item.fetched.상호);
                // ⭐ 번역 결과 가져오기
                const trans = translationResults[i] || {};

                // 기존 saveCompany API 활용 (businessNumber 기준 UPDATE)
                const res = await fetch(`${API_BASE}/api/legacy/company`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        businessNumber: item.businessNumber,
                        companyType: companyType,
                        companyName: item.fetched.상호,
                        ceoName: item.fetched.대표자,
                        address: item.fetched.주소,
                        tel: item.fetched.전화번호 || '',
                        // ⭐ 영문 정보 추가
                        companyNameEng: trans.companyNameEng || '',
                        ceoNameEng: trans.ceoNameEng || '',
                        addressEng: trans.addressEng || ''
                    })
                });
                if (res.ok) successCount++;
            } catch (err) {
                console.error('Save error:', err);
            }
            processed++;
            setProgress({ current: processed, total: toApply.length, action: '적용' });
        }

        setLoading(false);
        setProgress({ current: 0, total: 0, action: '' });
        setIsHomeTaxModalOpen(false);
        setHomeTaxCompareData(null);
        setSelectedHomeTaxIndex(0);
        fetchCompanyList();
        alert(`${successCount}건의 홈택스 자료가 저장되었습니다.`);
    };

    // 1단계: 체크된 항목을 현재 데이터(좌측 그리드)에 반영 (미리보기)
    const handleReflectCheckedItems = () => {
        if (!homeTaxCompareData) return;

        // 체크된 항목 중 조회 성공한 것만 필터
        const toReflect = checkedItems.filter(idx =>
            homeTaxCompareData[idx]?.fetched && !homeTaxCompareData[idx]?.error
        );

        if (toReflect.length === 0) {
            alert('반영할 데이터가 없습니다. 체크된 항목 중 조회 성공한 건이 없습니다.');
            return;
        }

        // homeTaxCompareData의 current 값을 fetched 값으로 업데이트 (UI 미리보기)
        const updatedData = homeTaxCompareData.map((item, idx) => {
            if (toReflect.includes(idx) && item.fetched) {
                const companyType = getCompanyTypeForSave(item.fetched.법인개인, item.fetched.상호);
                return {
                    ...item,
                    current: {
                        ...item.current,
                        법인개인: companyType,
                        상호: item.fetched.상호,
                        대표자: item.fetched.대표자,
                        주소: item.fetched.주소,
                        전화번호: item.fetched.전화번호 || ''
                    }
                };
            }
            return item;
        });

        setHomeTaxCompareData(updatedData);
        setReflectedItems([...new Set([...reflectedItems, ...toReflect])]); // 반영된 항목 추가

        // 좌측 그리드 갱신
        if (window.AUIGrid && homeTaxLeftGridId.current) {
            const leftData = updatedData.map((item, idx) => ({
                no: idx + 1,
                구분: item.current?.법인개인 || '',
                사업자번호: item.businessNumber,
                상호: item.current?.상호 || '',
                대표자: item.current?.대표자 || '',
                주소: item.current?.주소 || '',
                전화번호: item.current?.전화번호 || '',
                // 반영된 항목 표시
                _reflected: toReflect.includes(idx) || reflectedItems.includes(idx)
            }));
            window.AUIGrid.setGridData(homeTaxLeftGridId.current, leftData);
        }

        alert(`${toReflect.length}건이 현재 데이터에 반영되었습니다. 확인 후 'DB 저장' 버튼을 눌러 저장하세요.`);
    };

    // 2단계: 반영된 항목만 DB에 저장
    const handleSaveReflectedItems = async () => {
        console.log('🔍 [DB저장] reflectedItems:', reflectedItems);
        console.log('🔍 [DB저장] homeTaxCompareData:', homeTaxCompareData);

        if (!homeTaxCompareData || reflectedItems.length === 0) {
            alert('저장할 데이터가 없습니다. 먼저 "반영" 버튼을 눌러 데이터를 반영하세요.');
            return;
        }

        // 반영된 항목만 저장
        const toSave = reflectedItems
            .filter(idx => {
                const item = homeTaxCompareData[idx];
                console.log(`🔍 [DB저장] 항목 ${idx}:`, item);
                console.log(`🔍 [DB저장] 항목 ${idx} current:`, item?.current);
                return item?.current;
            })
            .map(idx => homeTaxCompareData[idx]);

        console.log('🔍 [DB저장] toSave:', toSave);

        if (toSave.length === 0) {
            alert('저장할 데이터가 없습니다. (current 데이터 없음)');
            return;
        }

        setLoading(true);
        setProgress({ current: 0, total: toSave.length, action: '저장' });

        let successCount = 0;
        let processed = 0;

        for (const item of toSave) {
            try {
                // 🔧 번역 결과 가져오기 (해당 인덱스의 번역 결과)
                const itemIdx = reflectedItems.find(idx => homeTaxCompareData[idx] === item);
                const trans = itemIdx !== undefined ? translationResults[itemIdx] : null;

                const requestBody = {
                    businessNumber: item.businessNumber,
                    groupCode: localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode'), // ⚠️ 필수 - 없으면 백엔드에서 오류
                    companyType: getCompanyTypeForSave(item.current.법인개인, item.current.상호), // A 또는 B
                    companyName: item.current.상호,
                    ceoName: item.current.대표자,
                    address: item.current.주소,
                    tel: item.current.전화번호 || '',
                    // ⭐ 영문 정보 추가 (번역 결과가 있으면 포함)
                    companyNameEng: trans?.companyNameEng || '',
                    ceoNameEng: trans?.ceoNameEng || '',
                    addressEng: trans?.addressEng || ''
                };
                console.log('📤 [DB저장] 요청 데이터:', requestBody);

                const res = await fetch(`${API_BASE}/api/legacy/company`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                });

                const result = await res.json();
                console.log('📥 [DB저장] 응답:', result);

                if (res.ok && result.success) {
                    successCount++;
                } else {
                    console.error('❌ [DB저장] 실패:', result);
                }
            } catch (err) {
                console.error('Save error:', err);
            }
            processed++;
            setProgress({ current: processed, total: toSave.length, action: '저장' });
        }

        setLoading(false);
        setProgress({ current: 0, total: 0, action: '' });
        setIsHomeTaxModalOpen(false);
        setHomeTaxCompareData(null);
        setSelectedHomeTaxIndex(0);
        setReflectedItems([]);
        fetchCompanyList();
        alert(`${successCount}건의 데이터가 DB에 저장되었습니다.`);
    };

    // 영문 변환 핸들러 (Gemini API) - 다중 처리
    const handleTranslateToEnglish = async () => {
        if (!homeTaxCompareData || homeTaxCompareData.length === 0) return;
        if (checkedItems.length === 0) {
            alert('번역할 항목을 선택해주세요.');
            return;
        }

        setIsTranslating(true);
        setProgress({ current: 0, total: checkedItems.length, action: '번역' });
        const results = { ...translationResults };
        let successCount = 0;
        let failCount = 0;
        let processed = 0;

        for (const idx of checkedItems) {
            const item = homeTaxCompareData[idx];
            if (!item?.fetched) {
                processed++;
                setProgress({ current: processed, total: checkedItems.length, action: '번역' });
                continue;
            }

            try {
                const res = await fetch(`${API_BASE}/api/legacy/translate-to-english`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        companyName: item.fetched.상호 || '',
                        ceoName: item.fetched.대표자 || '',
                        address: item.fetched.주소 || ''
                    })
                });

                const json = await res.json();
                if (json.success && json.data) {
                    results[idx] = {
                        companyNameEng: json.data.companyNameEng || '',
                        ceoNameEng: json.data.ceoNameEng || '',
                        addressEng: json.data.addressEng || ''
                    };
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (err) {
                console.error('Translation error:', err);
                failCount++;
            }
            processed++;
            setProgress({ current: processed, total: checkedItems.length, action: '번역' });
        }

        setTranslationResults(results);

        // 현재 선택된 항목의 번역 결과를 표시
        if (results[selectedHomeTaxIndex]) {
            setTranslationData(results[selectedHomeTaxIndex]);
        }

        setIsTranslating(false);
        setProgress({ current: 0, total: 0, action: '' });
        alert(`번역 완료: 성공 ${successCount}건, 실패 ${failCount}건`);
    };

    // 영문 데이터 저장 핸들러 - 다중 처리
    const handleSaveEnglishData = async () => {
        if (!homeTaxCompareData || homeTaxCompareData.length === 0) return;

        // 체크된 항목 중 번역 결과가 있는 것만 저장
        const itemsToSave = checkedItems.filter(idx => translationResults[idx]);

        if (itemsToSave.length === 0) {
            alert('저장할 번역 결과가 없습니다. 먼저 번역을 실행해주세요.');
            return;
        }

        setLoading(true);
        setProgress({ current: 0, total: itemsToSave.length, action: '저장' });
        let successCount = 0;
        let failCount = 0;
        let processed = 0;

        for (const idx of itemsToSave) {
            const item = homeTaxCompareData[idx];
            const trans = translationResults[idx];

            if (!item?.fetched || !trans) {
                processed++;
                setProgress({ current: processed, total: itemsToSave.length, action: '저장' });
                continue;
            }

            try {
                const res = await fetch(`${API_BASE}/api/legacy/company/english`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        businessNumber: item.businessNumber?.replace(/[^0-9]/g, '') || '',
                        companyNameEng: trans.companyNameEng,
                        ceoNameEng: trans.ceoNameEng,
                        addressEng: trans.addressEng
                    })
                });

                const json = await res.json();
                if (json.success) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (err) {
                console.error('Save error:', err);
                failCount++;
            }
            processed++;
            setProgress({ current: processed, total: itemsToSave.length, action: '저장' });
        }

        setLoading(false);
        setProgress({ current: 0, total: 0, action: '' });
        fetchCompanyList();
        alert(`영문 정보 저장 완료: 성공 ${successCount}건, 실패 ${failCount}건`);
    };

    useEffect(() => {
        fetchCompanyList();
    }, []);

    // 일괄변경 모달 열기 핸들러
    const handleOpenBatchUpdateModal = () => {
        if (!window.AUIGrid || !window.AUIGrid.isCreated(`#${gridId}`)) return;

        const checkedRows = window.AUIGrid.getCheckedRowItems(`#${gridId}`);
        if (!checkedRows || checkedRows.length === 0) {
            alert('일괄변경할 거래처를 선택해주세요.');
            return;
        }

        // 모달 열기 전 초기화
        setBatchUpdateData({ gwan: '', rate: '', saleRate: '' });
        setIsBatchUpdateModalOpen(true);
    };

    // 일괄변경 실행 핸들러
    const handleBatchUpdate = async () => {
        if (!window.AUIGrid || !window.AUIGrid.isCreated(`#${gridId}`)) return;

        const checkedRows = window.AUIGrid.getCheckedRowItems(`#${gridId}`);
        if (!checkedRows || checkedRows.length === 0) {
            alert('선택된 거래처가 없습니다.');
            return;
        }

        const { gwan, rate, saleRate } = batchUpdateData;

        // 최소 하나의 필드라도 입력되었는지 확인
        if (!gwan && !rate && !saleRate) {
            alert('변경할 항목을 최소 1개 이상 입력해주세요.');
            return;
        }

        const companyNos = checkedRows.map(r => r.item.COMPANY_NO).filter(Boolean);
        if (companyNos.length === 0) {
            alert('유효한 거래처 번호가 없습니다.');
            return;
        }

        const groupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode');
        if (!groupCode) {
            alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
            return;
        }

        if (!window.confirm(`선택한 ${companyNos.length}건의 거래처를 일괄변경 하시겠습니까?`)) {
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/legacy/companies/batch-update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    companyNos,
                    groupCode,
                    gwan: gwan || undefined,
                    rate: rate ? parseFloat(rate) : undefined,
                    saleRate: saleRate ? parseFloat(saleRate) : undefined
                })
            });

            const json = await res.json();
            if (json.success) {
                alert(`${json.updatedCount}건 일괄변경 완료!`);
                setIsBatchUpdateModalOpen(false);
                fetchCompanyList(); // 그리드 새로고침
            } else {
                alert(`오류: ${json.error}`);
            }
        } catch (err) {
            console.error('Batch update error:', err);
            alert('일괄변경 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 법인개인 유형 변환 함수
    const convertCompanyType = (type, companyName) => {
        if (!type && !companyName) return '-';
        // 홈택스 법인개인 값: "영리법인의본점", "개인" 등
        if (type?.includes('법인')) return '법인';
        // 상호명에 (주), (유), (합), (사) 포함시 법인
        if (companyName && /\(주\)|\(유\)|\(합\)|\(사\)|주식회사|유한회사/.test(companyName)) return '법인';
        return '개인';
    };

    // 홈택스 모달 AUIGrid 초기화
    useEffect(() => {
        if (!isHomeTaxModalOpen || !homeTaxCompareData || !window.AUIGrid) return;

        const leftData = homeTaxCompareData.map((item, idx) => ({
            _idx: idx,
            NO: idx + 1,
            COMPANY_TYPE: item.current.법인개인 || '-',
            BUSINESS_NUMBER: item.businessNumber,
            SANGHO: item.current.상호 || '-',
            CEO: item.current.대표자 || '-',
            ADDRESS: item.current.주소 || '-',
            TEL: item.current.전화번호 || '-'
        }));

        const rightData = homeTaxCompareData.map((item, idx) => ({
            _idx: idx,
            NO: idx + 1,
            COMPANY_TYPE: item.fetched ? convertCompanyType(item.fetched.법인개인, item.fetched.상호) : '-',
            SANGHO: item.fetched ? item.fetched.상호 : '조회실패',
            CEO: item.fetched ? item.fetched.대표자 : '-',
            ADDRESS: item.fetched ? item.fetched.주소 : '-',
            TEL: item.fetched ? item.fetched.전화번호 : '-',
            STATUS: item.applied ? '적용' : item.error ? '오류' : '대기'
        }));

        const leftColumns = [
            { dataField: "NO", headerText: "No", width: 30 },
            { dataField: "COMPANY_TYPE", headerText: "구분", width: 40 },
            { dataField: "BUSINESS_NUMBER", headerText: "사업자번호", width: 90 },
            { dataField: "SANGHO", headerText: "상호", width: 120, style: "left-align" },
            { dataField: "CEO", headerText: "대표자", width: 80 },
            { dataField: "ADDRESS", headerText: "주소", style: "left-align" },
            { dataField: "TEL", headerText: "전화번호", width: 90 }
        ];

        const rightColumns = [
            { dataField: "NO", headerText: "No", width: 30 },
            { dataField: "COMPANY_TYPE", headerText: "구분", width: 40 },
            { dataField: "SANGHO", headerText: "상호", width: 120, style: "left-align" },
            { dataField: "CEO", headerText: "대표자", width: 80 },
            { dataField: "ADDRESS", headerText: "주소", style: "left-align" },
            { dataField: "TEL", headerText: "전화번호", width: 90 },
            {
                dataField: "STATUS", headerText: "상태", width: 40,
                labelFunction: (rowIndex, columnIndex, value) => {
                    if (value === '적용') return '<span style="color:#059669">적용</span>';
                    if (value === '오류') return '<span style="color:#dc2626">오류</span>';
                    return '<span style="color:#6b7280">대기</span>';
                }
            }
        ];

        const leftGridProps = {
            headerHeight: 24,
            rowHeight: 22,
            showRowNumColumn: false,
            showStateColumn: false,
            showRowCheckColumn: true,
            rowCheckColumnWidth: 30,
            selectionMode: "singleRow",
            enableSorting: false,
            softRemoveRowMode: false
        };

        const rightGridProps = {
            headerHeight: 24,
            rowHeight: 22,
            showRowNumColumn: false,
            showStateColumn: false,
            selectionMode: "singleRow",
            enableSorting: false,
            softRemoveRowMode: false
        };

        // 왼쪽 그리드 생성
        setTimeout(() => {
            if (window.AUIGrid.isCreated(`#${homeTaxLeftGridId}`)) {
                window.AUIGrid.destroy(`#${homeTaxLeftGridId}`);
            }
            if (document.getElementById(homeTaxLeftGridId)) {
                window.AUIGrid.create(`#${homeTaxLeftGridId}`, leftColumns, leftGridProps);
                window.AUIGrid.setGridData(`#${homeTaxLeftGridId}`, leftData);

                // 모든 행 체크
                window.AUIGrid.setAllCheckedRows(`#${homeTaxLeftGridId}`, true);

                window.AUIGrid.bind(`#${homeTaxLeftGridId}`, "cellClick", (e) => {
                    setSelectedHomeTaxIndex(e.item._idx);
                    if (window.AUIGrid.isCreated(`#${homeTaxRightGridId}`)) {
                        window.AUIGrid.setSelectionByIndex(`#${homeTaxRightGridId}`, e.item._idx);
                    }
                });

                // 체크 변경 시 상태 업데이트
                window.AUIGrid.bind(`#${homeTaxLeftGridId}`, "rowCheckClick", (e) => {
                    const checked = window.AUIGrid.getCheckedRowItems(`#${homeTaxLeftGridId}`);
                    setCheckedItems(checked.map(c => c.item._idx));
                });
                window.AUIGrid.bind(`#${homeTaxLeftGridId}`, "headerClick", (e) => {
                    // 헤더 체크박스 클릭 시
                    setTimeout(() => {
                        const checked = window.AUIGrid.getCheckedRowItems(`#${homeTaxLeftGridId}`);
                        setCheckedItems(checked.map(c => c.item._idx));
                    }, 50);
                });

                if (selectedHomeTaxIndex >= 0) {
                    window.AUIGrid.setSelectionByIndex(`#${homeTaxLeftGridId}`, selectedHomeTaxIndex);
                }
            }
        }, 100);

        // 오른쪽 그리드 생성
        setTimeout(() => {
            if (window.AUIGrid.isCreated(`#${homeTaxRightGridId}`)) {
                window.AUIGrid.destroy(`#${homeTaxRightGridId}`);
            }
            if (document.getElementById(homeTaxRightGridId)) {
                window.AUIGrid.create(`#${homeTaxRightGridId}`, rightColumns, rightGridProps);
                window.AUIGrid.setGridData(`#${homeTaxRightGridId}`, rightData);
                window.AUIGrid.bind(`#${homeTaxRightGridId}`, "cellClick", (e) => {
                    setSelectedHomeTaxIndex(e.item._idx);
                    if (window.AUIGrid.isCreated(`#${homeTaxLeftGridId}`)) {
                        window.AUIGrid.setSelectionByIndex(`#${homeTaxLeftGridId}`, e.item._idx);
                    }
                });
                if (selectedHomeTaxIndex >= 0) {
                    window.AUIGrid.setSelectionByIndex(`#${homeTaxRightGridId}`, selectedHomeTaxIndex);
                }
            }
        }, 100);

        return () => {
            if (window.AUIGrid.isCreated(`#${homeTaxLeftGridId}`)) {
                window.AUIGrid.destroy(`#${homeTaxLeftGridId}`);
            }
            if (window.AUIGrid.isCreated(`#${homeTaxRightGridId}`)) {
                window.AUIGrid.destroy(`#${homeTaxRightGridId}`);
            }
        };
    }, [isHomeTaxModalOpen, homeTaxCompareData]);


    // 2. Initialize AUIGrid
    useEffect(() => {
        if (!window.AUIGrid) return;

        const columnLayout = [
            { dataField: "COMPANY_NO", headerText: "No", width: 40, labelFunction: function (rowIndex, columnIndex, value, headerText, item, dataField, cItem) { return rowIndex + 1; } },
            { dataField: "GWAN", headerText: "관세사", width: 80 },
            { dataField: "COMPANY_TYPE", headerText: "구분", width: 50, labelFunction: (rowIndex, columnIndex, value) => value === 'A' ? '법인' : value === 'B' ? '개인' : value },
            { dataField: "SANGHO", headerText: "상호", width: 120, style: "left-align" },
            { dataField: "BUSINESS_NUMBER", headerText: "사업자번호", width: 90 },
            { dataField: "TEL", headerText: "전화번호", width: 90 },
            { dataField: "FAX", headerText: "팩스번호", width: 90 },
            { dataField: "RATE", headerText: "환급요율", width: 70, dataType: "numeric", formatString: "#,##0" },
            { dataField: "SALE_RATE", headerText: "판매요율", width: 70, dataType: "numeric", formatString: "#,##0" },
            { dataField: "ADDRESS", headerText: "주소", style: "left-align" }
        ];

        const gridProps = {
            headerHeight: 30,
            rowHeight: 25,
            fontSize: 12,
            editable: false,
            selectionMode: "singleRow",
            showRowNumColumn: false,
            showRowCheckColumn: true, // 체크박스 컬럼 표시
            showAllCheckBox: false, // 헤더의 전체선택 체크박스 비활성화
            rowCheckColumnWidth: 30,
            showRowBorder: true,
            showColumnBorder: true,
            hScrollEnable: true,
            vScrollEnable: true,
            enableFilter: true,
            showInlineFilter: true,
            useContextMenu: true,
            enableRightDownFocus: true,
            filterLayerWidth: 320,
            filterLayerHeight: 300,
            fillColumnSizeMode: true,
        };

        if (window.AUIGrid.isCreated(`#${gridId}`)) {
            window.AUIGrid.destroy(`#${gridId}`);
        }

        window.AUIGrid.create(`#${gridId}`, columnLayout, gridProps);



        // Bind Selection Event
        // Bind Cell Click Event (More reliable than selectionChange for user interaction)
        window.AUIGrid.bind(`#${gridId}`, "cellClick", (event) => {
            const item = event.item;
            console.log('🖱️ Grid Cell Click:', item);
            mapItemToForm(item);

            // 첨부파일 조회를 위한 거래처 번호 저장 및 조회
            const companyNo = item.COMPANY_NO || item.company_no;
            if (companyNo) {
                setSelectedCompanyNo(companyNo);
                fetchAttachments(companyNo);
            }
        });

        // 키보드 화살표로 이동 시에도 데이터 표시
        window.AUIGrid.bind(`#${gridId}`, "selectionChange", (event) => {
            const selectedItems = event.selectedItems;
            if (selectedItems && selectedItems.length > 0) {
                // AUIGrid의 selectedItems는 { item: {...} } 형태
                const item = selectedItems[0].item || selectedItems[0];
                console.log('⌨️ Grid Selection Change:', item);
                mapItemToForm(item);

                const companyNo = item.COMPANY_NO || item.company_no;
                if (companyNo) {
                    setSelectedCompanyNo(companyNo);
                    fetchAttachments(companyNo);
                }
            }
        });

        // Load Initial Data
        if (companyList.length > 0) {
            window.AUIGrid.setGridData(`#${gridId}`, companyList);
        }

        return () => {
            if (window.AUIGrid.isCreated(`#${gridId}`)) {
                window.AUIGrid.destroy(`#${gridId}`);
            }
        };
    }, []); // Run once on mount

    // 2.5 Initial Data Load - 🔧 FIX: 마운트 시 회사 목록 자동 로드
    useEffect(() => {
        // 그리드 생성 후 데이터 로드
        const loadInitialData = async () => {
            if (window.AUIGrid && window.AUIGrid.isCreated(`#${gridId}`)) {
                await fetchCompanyList();
            }
        };

        // 약간의 딜레이 후 로드 (그리드 완전히 초기화 대기)
        const timer = setTimeout(loadInitialData, 100);
        return () => clearTimeout(timer);
    }, []);

    // 3. Auto Resize Listener
    useEffect(() => {
        const handleResize = () => {
            if (window.AUIGrid && window.AUIGrid.isCreated(`#${gridId}`)) {
                window.AUIGrid.resize(`#${gridId}`);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const mapItemToForm = (d) => {
        // Robust mapping: handle both Upper and Lower case keys from DB/Grid
        const getVal = (key) => d[key] || d[key.toLowerCase()] || d[key.toUpperCase()] || '';

        setFormData(prev => ({
            ...prev,
            businessNumber: getVal('BUSINESS_NUMBER'),
            companyType: (getVal('COMPANY_TYPE') === 'A' || getVal('COMPANY_TYPE') === '법인') ? '법인' : '개인',
            companyName: getVal('SANGHO'),
            companyNameEng: getVal('SANGHO_ENG'),
            ceoName: getVal('CEO_NAME'),
            ceoNameEng: getVal('CEO_NAME_ENG'),
            address: getVal('ADDRESS'),
            addressEng: getVal('ADDRESS_ENG'),
            tel: getVal('TEL'),
            fax: getVal('FAX'),
            gwan: getVal('GWAN') || '선택',
            refundRate: getVal('RATE') !== '' && getVal('RATE') !== undefined ? String(getVal('RATE')) : '30',
            saleRate: getVal('SALE_RATE') !== '' && getVal('SALE_RATE') !== undefined ? String(getVal('SALE_RATE')) : '50',
            contractSeal: getVal('SIGN') || null,
            invoiceSeal: getVal('SIGN_IV') || null
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // 파일 업로드 핸들러
    const handleFileUpload = (e, fieldName) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 이미지 미리보기 (로컬)
        const reader = new FileReader();
        reader.onload = (ev) => {
            setFormData(prev => ({ ...prev, [fieldName]: ev.target.result }));
        };
        reader.readAsDataURL(file);
    };

    // 드래그앤드롭 핸들러
    const handleDrop = (e, fieldName) => {
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            setFormData(prev => ({ ...prev, [fieldName]: ev.target.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleSearch = async () => {
        if (!formData.businessNumber) return;
        try {
            const res = await fetch(`${API_BASE}/api/legacy/company/${formData.businessNumber}`);
            const json = await res.json();
            if (json.success && json.data) {
                mapItemToForm(json.data);
                setMessage('🔍 거래처 정보를 불러왔습니다.');
            } else {
                setMessage('⚠️ 등록된 거래처가 없습니다. 신규 등록합니다.');
            }
        } catch (err) {
            console.error(err);
            alert('조회 중 오류가 발생했습니다.');
        }
    };

    const handleSave = async () => {
        if (!formData.businessNumber) {
            alert('사업자 번호는 필수입니다.');
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/legacy/company`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const json = await res.json();
            if (json.success) {
                alert(`저장 성공! (${json.type})`);
                fetchCompanyList(); // Refresh Grid
            } else {
                alert(`저장 실패: ${json.error}`);
            }
        } catch (err) {
            console.error(err);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Header */}
            <div className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0">
                <h1 className="text-lg font-bold flex items-center gap-2 text-gray-800">
                    <span className="w-1.5 h-6 bg-blue-600 inline-block rounded-sm"></span>
                    수출화주/거래처 관리
                </h1>
                <div className="flex gap-2">
                    <button
                        onClick={handleFetchHomeTax}
                        disabled={loading}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm rounded transition-colors shadow-sm disabled:opacity-50"
                    >
                        <Search size={14} /> 홈택스 자료
                    </button>
                    <button
                        onClick={fetchCompanyList}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded border border-gray-300 transition-colors"
                    >
                        <RefreshCw size={14} /> 새로고침
                    </button>
                    <button
                        onClick={handleOpenBatchUpdateModal}
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm rounded transition-colors shadow-sm"
                    >
                        📝 일괄변경
                    </button>
                    <button
                        onClick={() => setIsMigrationModalOpen(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors shadow-sm"
                    >
                        <Database size={14} /> 마이그레이션 (Excel)
                    </button>
                    <button
                        onClick={handleSave}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors shadow-sm"
                    >
                        <Save size={14} /> 저장
                    </button>
                </div>
            </div>

            {/* Split Content */}
            <div className="flex flex-1 overflow-hidden min-h-0">
                {/* Left: Grid */}
                <div className="w-[65%] flex flex-col border-r border-gray-300 bg-white">
                    <div className="p-2 border-b border-gray-200 bg-gray-50 text-sm font-semibold text-gray-600">
                        등록 리스트
                    </div>
                    <div className="flex-1" style={{ minHeight: 'calc(100vh - 150px)' }}>
                        <div id={gridId} style={{ width: '100%', height: '100%' }}></div>
                    </div>
                </div>

                {/* Right: Form */}
                <div className="flex-1 flex flex-col overflow-y-auto bg-white p-2">
                    <h2 className="text-base font-bold mb-4 border-b pb-2 flex items-center">
                        <span className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded mr-2">상세정보</span>
                        거래처 설정
                    </h2>

                    {/* Form Table */}
                    <div className="border-t-2 border-gray-600 mb-6 w-full max-w-4xl">
                        {/* Row 1 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">사업자 번호*</div>
                            <div className="flex-1 p-2 flex items-center">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        name="businessNumber"
                                        value={formData.businessNumber}
                                        onChange={handleChange}
                                        className="border border-gray-300 px-2 py-1 pr-8 w-full text-xs"
                                    />
                                    <button onClick={handleSearch} className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                        <Search size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">법인/개인</div>
                            <div className="flex-1 p-2">
                                <select
                                    name="companyType"
                                    value={formData.companyType}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                >
                                    <option value="법인">법인</option>
                                    <option value="개인">개인</option>
                                </select>
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">상호</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">상호(영문)</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="companyNameEng"
                                    value={formData.companyNameEng}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>

                        {/* Row 3 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">대표자</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="ceoName"
                                    value={formData.ceoName}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">대표자(영문)</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="ceoNameEng"
                                    value={formData.ceoNameEng}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>

                        {/* Row 4 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">주소</div>
                            <div className="flex-[3] p-2">
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">주소(영문)</div>
                            <div className="flex-[3] p-2">
                                <input
                                    type="text"
                                    name="addressEng"
                                    value={formData.addressEng}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>

                        {/* Row 5 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">전화번호</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="tel"
                                    value={formData.tel}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">팩스번호</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="fax"
                                    value={formData.fax}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>

                        {/* Row 6 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">관세사</div>
                            <div className="flex-[3] p-2">
                                <select
                                    name="gwan"
                                    value={formData.gwan}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full max-w-xs text-xs"
                                >
                                    <option value="선택">선택</option>
                                    <option value="경인 관세사무소">경인 관세사무소</option>
                                    <option value="성인 관세사무소">성인 관세사무소</option>
                                </select>
                            </div>
                        </div>

                        {/* Row 7 */}
                        <div className="flex border-b border-gray-200">
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">환급 요율</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="refundRate"
                                    value={formData.refundRate}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                            <div className="w-28 bg-gray-100 py-1 px-2 flex items-center font-medium text-xs">판매 요율</div>
                            <div className="flex-1 p-2">
                                <input
                                    type="text"
                                    name="saleRate"
                                    value={formData.saleRate}
                                    onChange={handleChange}
                                    className="border border-gray-300 px-2 py-1 w-full text-xs"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Seals Section */}
                    <div className="grid grid-cols-2 gap-6 mb-4 max-w-4xl">
                        {/* Contract Seal */}
                        <div className="border border-gray-200 p-1.5 bg-yellow-50/30 flex flex-col items-center">
                            <div className="mb-0.5 font-medium text-xs">계약서 직인</div>
                            <div
                                className="w-20 h-20 border-2 border-dashed border-gray-300 bg-white mb-0.5 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                                onDrop={(e) => handleDrop(e, 'contractSeal')}
                                onDragOver={handleDragOver}
                                onClick={() => document.getElementById('contractSealInput').click()}
                            >
                                {formData.contractSeal ? (
                                    <img
                                        src={
                                            formData.contractSeal.startsWith('data:')
                                                ? formData.contractSeal
                                                : formData.contractSeal.startsWith('http')
                                                    ? formData.contractSeal
                                                    : `${API_BASE}/api/legacy/proxy-image?path=${encodeURIComponent(formData.contractSeal)}`
                                        }
                                        alt="Seal"
                                        className="max-w-full max-h-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <span className="text-gray-400 text-[9px] text-center">클릭 또는<br />드래그</span>
                                )}
                            </div>
                            <input id="contractSealInput" type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'contractSeal')} />
                            {formData.contractSeal && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, contractSeal: null })); }}
                                    className="text-[9px] text-red-500 hover:text-red-700"
                                >
                                    ✕ 삭제
                                </button>
                            )}
                        </div>

                        {/* Invoice Seal */}
                        <div className="border border-gray-200 p-1.5 bg-yellow-50/30 flex flex-col items-center">
                            <div className="mb-0.5 font-medium text-xs">인보이스 직인</div>
                            <div
                                className="w-20 h-20 border-2 border-dashed border-gray-300 bg-white mb-0.5 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                                onDrop={(e) => handleDrop(e, 'invoiceSeal')}
                                onDragOver={handleDragOver}
                                onClick={() => document.getElementById('invoiceSealInput').click()}
                            >
                                {formData.invoiceSeal ? (
                                    <img
                                        src={
                                            formData.invoiceSeal.startsWith('data:')
                                                ? formData.invoiceSeal
                                                : formData.invoiceSeal.startsWith('http')
                                                    ? formData.invoiceSeal
                                                    : `${API_BASE}/api/legacy/proxy-image?path=${encodeURIComponent(formData.invoiceSeal)}`
                                        }
                                        alt="Seal"
                                        className="max-w-full max-h-full object-contain"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <span className="text-gray-400 text-[9px] text-center">클릭 또는<br />드래그</span>
                                )}
                            </div>
                            <input id="invoiceSealInput" type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e, 'invoiceSeal')} />
                            {formData.invoiceSeal && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, invoiceSeal: null })); }}
                                    className="text-[9px] text-red-500 hover:text-red-700"
                                >
                                    ✕ 삭제
                                </button>
                            )}
                        </div>
                    </div>

                    {/* File List Section */}
                    <div className="mb-6 max-w-4xl">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-bold text-gray-800 flex items-center gap-2">
                                <span className="w-1 h-4 bg-gray-600 inline-block"></span>
                                첨부파일
                            </div>
                            <div className="flex gap-1 items-center">
                                <select
                                    id="uploadTypeSelect"
                                    className="text-xs border border-gray-300 rounded px-2 py-1"
                                    defaultValue="사업자등록증"
                                >
                                    {DOCUMENT_TYPES.map(typeName => (
                                        <option key={typeName} value={typeName}>{typeName}</option>
                                    ))}
                                </select>
                                <label className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1 cursor-pointer">
                                    <Upload size={12} /> 파일추가
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={(e) => {
                                            const typeName = document.getElementById('uploadTypeSelect')?.value || '기타';
                                            handleAttachmentUpload(e, typeName);
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                        <table className="w-full text-sm border-t-2 border-gray-600">
                            <thead className="bg-gray-100 text-gray-700">
                                <tr>
                                    <th className="py-1.5 px-2 text-left w-8 border-b font-medium">No</th>
                                    <th className="py-1.5 px-2 text-left w-32 border-b font-medium">구분</th>
                                    <th className="py-1.5 px-2 text-left border-b font-medium">파일명</th>
                                    <th className="py-1.5 px-2 text-center w-12 border-b font-medium">다운</th>
                                    <th className="py-1.5 px-2 text-center w-12 border-b font-medium">삭제</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attachments.length > 0 ? (
                                    attachments.map((file, idx) => (
                                        <tr key={file.id} className="border-b border-gray-200 hover:bg-blue-50">
                                            <td className="py-1.5 px-2">{idx + 1}</td>
                                            <td className="py-1 px-1">
                                                <select
                                                    value={file.typeName || '기타'}
                                                    onChange={(e) => handleAttachmentTypeChange(file.id, e.target.value)}
                                                    className="text-xs border border-gray-300 rounded px-1 py-0.5 w-full"
                                                >
                                                    {DOCUMENT_TYPES.map(typeName => (
                                                        <option key={typeName} value={typeName}>{typeName}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="py-1.5 px-2 truncate max-w-[200px]" title={file.originalName}>{file.originalName || <span className="text-gray-400">-</span>}</td>
                                            <td className="py-1.5 px-2 text-center">
                                                <button
                                                    onClick={() => handleDownload(file)}
                                                    className="text-blue-600 hover:underline text-xs"
                                                >다운</button>
                                            </td>
                                            <td className="py-1.5 px-2 text-center">
                                                <button
                                                    onClick={() => handleAttachmentDelete(file.id)}
                                                    className="text-red-500 hover:underline text-xs"
                                                >삭제</button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="py-2 text-center text-gray-400 text-xs">
                                            {selectedCompanyNo ? '등록된 파일이 없습니다.' : '거래처를 선택하세요.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Info Footer */}
                    <div className="border-t border-b border-gray-300 py-2 mb-6 text-sm flex gap-4 text-gray-600 max-w-4xl">
                        <div>등록정보: {new Date().toISOString().split('T')[0]} | jmkim</div>
                        <div>수정정보: {new Date().toISOString().split('T')[0]} | jmkim</div>
                    </div>
                </div>
            </div>

            {/* Migration Modal */}
            {isMigrationModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-10">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col relative">
                        <button
                            onClick={() => {
                                setIsMigrationModalOpen(false);
                                fetchCompanyList();
                            }}
                            className="absolute top-4 right-4 text-gray-500 hover:text-black z-10"
                        >
                            <X size={24} />
                        </button>
                        <div className="p-4 border-b flex-shrink-0">
                            <h2 className="text-xl font-bold">엑셀 마이그레이션 도구</h2>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <ExcelImageExtractor onRegistrationComplete={(registeredBrns) => {
                                setIsMigrationModalOpen(false);
                                console.log('🔍 [DEBUG] registeredBrns:', registeredBrns);
                                if (registeredBrns && registeredBrns.length > 0) {
                                    // 🔧 FIX: groupCode 필수
                                    const groupCode = localStorage.getItem('groupCode') || sessionStorage.getItem('groupCode');
                                    if (!groupCode) {
                                        alert('로그인 정보가 없습니다.');
                                        return;
                                    }
                                    console.log('🔍 [DEBUG] groupCode:', groupCode);
                                    fetch(`${API_BASE}/api/legacy/companies?groupCode=${groupCode}`)
                                        .then(res => res.json())
                                        .then(json => {
                                            console.log('🔍 [DEBUG] API response:', json.success, 'count:', json.list?.length);
                                            if (json.success) {
                                                const fullList = json.list || [];
                                                // 🔧 FIX: 하이픈 제거 후 비교 (registeredBrns는 brnRaw 형식일 수 있음)
                                                const normalizedBrns = registeredBrns.map(brn => brn.replace(/-/g, ''));
                                                console.log('🔍 [DEBUG] normalizedBrns:', normalizedBrns);
                                                console.log('🔍 [DEBUG] DB BUSINESS_NUMBERs (first 5):', fullList.slice(0, 5).map(c => c.BUSINESS_NUMBER));
                                                const filtered = fullList.filter(c => {
                                                    const dbBrn = (c.BUSINESS_NUMBER || '').replace(/-/g, '');
                                                    return normalizedBrns.includes(dbBrn);
                                                });
                                                console.log('🔍 [DEBUG] filtered count:', filtered.length);
                                                setCompanyList(fullList);
                                                if (window.AUIGrid && window.AUIGrid.isCreated(`#${gridId}`)) {
                                                    window.AUIGrid.setGridData(`#${gridId}`, filtered);
                                                    setMessage(`✅ 등록된 거래처 ${filtered.length}건을 표시합니다.`);
                                                }
                                            }
                                        });
                                } else {
                                    fetchCompanyList();
                                }
                            }} />
                        </div>
                    </div>
                </div>
            )}

            {/* 홈택스 비교 모달 - 회색 심플 테마 */}
            {isHomeTaxModalOpen && homeTaxCompareData && Array.isArray(homeTaxCompareData) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-[1400px] h-[85vh] flex flex-col">
                        {/* 헤더 */}
                        <div className="flex items-center justify-between px-6 py-2 border-b border-gray-200">
                            <h2 className="text-sm font-medium text-gray-700">
                                국세청(홈택스) 자료 비교 <span className="text-gray-500">({homeTaxCompareData.length}건)</span>
                            </h2>
                            <button
                                onClick={() => { setIsHomeTaxModalOpen(false); setHomeTaxCompareData(null); setSelectedHomeTaxIndex(0); }}
                                className="text-gray-400 hover:text-gray-600 text-xl"
                            >
                                ✕
                            </button>
                        </div>

                        {/* 그리드 영역 */}
                        <div className="flex-1 flex overflow-hidden border-b border-gray-200">
                            {/* 왼쪽 그리드: 현재 데이터 */}
                            <div className="w-1/2 flex flex-col border-r border-gray-200">
                                <div className="px-3 py-1 border-b border-gray-200 text-xs text-gray-500">
                                    현재 데이터 (DB)
                                </div>
                                <div id={homeTaxLeftGridId} ref={homeTaxLeftGridRef} className="flex-1" style={{ minHeight: '150px' }}></div>
                            </div>

                            {/* 오른쪽 그리드: 홈택스 조회 결과 */}
                            <div className="w-1/2 flex flex-col">
                                <div className="px-3 py-1 border-b border-gray-200 text-xs text-gray-500">
                                    홈택스 조회 결과
                                </div>
                                <div id={homeTaxRightGridId} ref={homeTaxRightGridRef} className="flex-1" style={{ minHeight: '150px' }}></div>
                            </div>
                        </div>

                        {/* 하단 상세 비교 폼 - 좌우 분리 */}
                        {homeTaxCompareData[selectedHomeTaxIndex] && (
                            <div className="bg-white px-4 py-2">
                                <div className="flex gap-4">
                                    {/* 왼쪽: 현재 데이터 폼 */}
                                    <div className="flex-1">
                                        <div className="text-xs text-gray-500 mb-1">현재 데이터 (DB)</div>
                                        <div className="space-y-1">
                                            {['법인개인', '상호', '대표자', '주소', '전화번호', '팩스번호'].map(field => {
                                                const item = homeTaxCompareData[selectedHomeTaxIndex];
                                                const current = item.current[field] || '';
                                                const fetched = item.fetched?.[field] || '';
                                                const isDifferent = current !== fetched && fetched;

                                                return (
                                                    <div key={field} className="flex items-center gap-1">
                                                        <span className="w-12 text-[10px] text-gray-400">{field}</span>
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={current}
                                                            className="flex-1 px-1.5 py-0.5 text-xs border rounded bg-gray-50 border-gray-200"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* 오른쪽: 홈택스 데이터 폼 */}
                                    <div className="flex-1">
                                        <div className="text-xs text-gray-500 mb-1">홈택스 조회 결과</div>
                                        <div className="space-y-1">
                                            {['법인개인', '상호', '대표자', '주소', '전화번호', '팩스번호'].map(field => {
                                                const item = homeTaxCompareData[selectedHomeTaxIndex];
                                                const current = item.current[field] || '';
                                                const fetched = item.fetched?.[field] || '';
                                                const isDifferent = current !== fetched && fetched;

                                                return (
                                                    <div key={field} className="flex items-center gap-1">
                                                        <span className="w-12 text-[10px] text-gray-400">{field}</span>
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={fetched}
                                                            className="flex-1 px-1.5 py-0.5 text-xs border rounded bg-gray-50 border-gray-200"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 영문 변환 섹션 */}
                        {homeTaxCompareData && homeTaxCompareData.length > 0 && homeTaxCompareData[selectedHomeTaxIndex]?.fetched && (
                            <div className="mx-4 mb-2 p-2 border border-gray-200 rounded">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">영문 변환</span>
                                        {progress.total > 0 && (
                                            <span className="text-xs text-blue-600 font-medium">
                                                {progress.action} 중... {progress.current}/{progress.total}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleTranslateToEnglish}
                                            disabled={isTranslating || loading}
                                            className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs transition-colors disabled:opacity-50"
                                        >
                                            {isTranslating ? `번역 ${progress.current}/${progress.total}` : '번역'}
                                        </button>
                                        <button
                                            onClick={handleSaveEnglishData}
                                            disabled={loading || isTranslating || Object.keys(translationResults).length === 0}
                                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors disabled:opacity-50"
                                        >
                                            {loading && progress.action === '저장' ? `저장 ${progress.current}/${progress.total}` : '저장'}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    {/* 왼쪽: 한글 원본 */}
                                    <div className="space-y-1">
                                        <div className="text-[10px] text-gray-500 mb-1">한글 원본</div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">상호</span>
                                            <input type="text" readOnly value={homeTaxCompareData[selectedHomeTaxIndex]?.fetched?.상호 || ''}
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-gray-50 border border-gray-300 rounded text-gray-700" />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">대표</span>
                                            <input type="text" readOnly value={homeTaxCompareData[selectedHomeTaxIndex]?.fetched?.대표자 || ''}
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-gray-50 border border-gray-300 rounded text-gray-700" />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">주소</span>
                                            <input type="text" readOnly value={homeTaxCompareData[selectedHomeTaxIndex]?.fetched?.주소 || ''}
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-gray-50 border border-gray-300 rounded text-gray-700" />
                                        </div>
                                    </div>

                                    {/* 오른쪽: 영문 번역 */}
                                    <div className="space-y-1">
                                        <div className="text-[10px] text-blue-600 mb-1">영문 번역</div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">Name</span>
                                            <input type="text"
                                                value={translationData.companyNameEng}
                                                onChange={(e) => setTranslationData(prev => ({ ...prev, companyNameEng: e.target.value }))}
                                                placeholder="Company Name"
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-gray-300 rounded text-gray-800 focus:border-blue-500" />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">CEO</span>
                                            <input type="text"
                                                value={translationData.ceoNameEng}
                                                onChange={(e) => setTranslationData(prev => ({ ...prev, ceoNameEng: e.target.value }))}
                                                placeholder="CEO Name"
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-gray-300 rounded text-gray-800 focus:border-blue-500" />
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="w-8 text-[10px] text-gray-500">Addr</span>
                                            <input type="text"
                                                value={translationData.addressEng}
                                                onChange={(e) => setTranslationData(prev => ({ ...prev, addressEng: e.target.value }))}
                                                placeholder="Address"
                                                className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-gray-300 rounded text-gray-800 focus:border-blue-500" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 푸터 버튼 */}
                        <div className="flex justify-between items-center px-6 py-2 border-t border-gray-200">
                            <div className="text-xs text-gray-500">
                                조회 성공: <span className="text-blue-600 font-medium">{homeTaxCompareData.filter(i => i.fetched).length}건</span>
                                {' / '}실패: <span className="text-red-500">{homeTaxCompareData.filter(i => i.error).length}건</span>
                            </div>
                            <div className="flex items-center gap-3">
                                {progress.total > 0 && (
                                    <span className="text-xs text-blue-600">
                                        {progress.action} 중... {progress.current}/{progress.total}
                                    </span>
                                )}
                                <button
                                    onClick={handleReflectCheckedItems}
                                    disabled={loading || checkedItems.filter(idx => homeTaxCompareData[idx]?.fetched).length === 0}
                                    className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
                                    title="체크된 항목의 홈택스 조회 결과를 현재 데이터(DB)에 미리보기로 반영합니다. 실제 저장은 'DB 저장' 버튼을 눌러야 합니다."
                                >
                                    홈택스 가져오기 ({checkedItems.filter(idx => homeTaxCompareData[idx]?.fetched).length}건)
                                </button>
                                <button
                                    onClick={handleSaveReflectedItems}
                                    disabled={loading || reflectedItems.length === 0}
                                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
                                    title="'홈택스 가져오기'로 반영된 데이터를 실제 DB에 저장합니다. 반영된 항목만 저장됩니다."
                                >
                                    {loading ? '저장 중...' : `DB 저장 (${reflectedItems.length}건)`}
                                </button>
                                <button
                                    onClick={() => { setIsHomeTaxModalOpen(false); setHomeTaxCompareData(null); setSelectedHomeTaxIndex(0); setTranslationResults({}); setReflectedItems([]); }}
                                    className="px-4 py-1.5 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs font-medium transition-colors"
                                    title="변경 사항을 저장하지 않고 창을 닫습니다."
                                >
                                    취소
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 일괄변경 모달 */}
            {isBatchUpdateModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-[400px]">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">📝 거래처 일괄변경</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            입력한 항목만 선택된 거래처에 일괄 적용됩니다.<br />
                            (빈 칸은 변경하지 않음)
                        </p>

                        <div className="space-y-3">
                            <div className="flex items-center">
                                <label className="w-24 text-sm font-medium text-gray-700">관세사</label>
                                <input
                                    type="text"
                                    value={batchUpdateData.gwan}
                                    onChange={(e) => setBatchUpdateData(prev => ({ ...prev, gwan: e.target.value }))}
                                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                                    placeholder="예: 김관세"
                                />
                            </div>
                            <div className="flex items-center">
                                <label className="w-24 text-sm font-medium text-gray-700">환급요율</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={batchUpdateData.rate}
                                    onChange={(e) => setBatchUpdateData(prev => ({ ...prev, rate: e.target.value }))}
                                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                                    placeholder="예: 30"
                                />
                                <span className="ml-2 text-gray-500 text-sm">%</span>
                            </div>
                            <div className="flex items-center">
                                <label className="w-24 text-sm font-medium text-gray-700">판매요율</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={batchUpdateData.saleRate}
                                    onChange={(e) => setBatchUpdateData(prev => ({ ...prev, saleRate: e.target.value }))}
                                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                                    placeholder="예: 50"
                                />
                                <span className="ml-2 text-gray-500 text-sm">%</span>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                            <button
                                onClick={() => setIsBatchUpdateModalOpen(false)}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleBatchUpdate}
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
                            >
                                {loading ? '처리중...' : '일괄변경'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 홈택스 확인 모달 */}
            {homeTaxConfirmItems.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] flex flex-col">
                        <div className="p-4 border-b bg-amber-50">
                            <h3 className="text-lg font-bold text-amber-800">🔍 홈택스 자료 조회 확인</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                아래 {homeTaxConfirmItems.length}건의 거래처에 대해 홈택스 정보를 조회합니다.
                            </p>
                        </div>

                        <div className="flex-1 overflow-auto p-4">
                            <table className="w-full text-sm border-collapse">
                                <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                        <th className="border px-2 py-2 text-left w-10">No</th>
                                        <th className="border px-2 py-2 text-left">상호</th>
                                        <th className="border px-2 py-2 text-left w-32">사업자번호</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {homeTaxConfirmItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="border px-2 py-1.5 text-center">{idx + 1}</td>
                                            <td className="border px-2 py-1.5">{item.SANGHO || '-'}</td>
                                            <td className="border px-2 py-1.5">{item.BUSINESS_NUMBER || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
                            <button
                                onClick={() => setHomeTaxConfirmItems([])}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleExecuteHomeTaxFetch}
                                disabled={loading}
                                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm disabled:opacity-50"
                            >
                                {loading ? '조회중...' : `홈택스 조회 (${homeTaxConfirmItems.length}건)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {message && (
                <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded shadow-lg animate-fade-in z-40">
                    {message}
                </div>
            )}
        </div>
    );
};

export default LegacyCustomerRegistration;
